import { motion } from "framer-motion";
import { Reveal, SectionTag, ArrowLink, EASE, MaskedLines } from "@/components/Reveal";

const STEPS = [
  { label: "Discover", copy: "Understand the business and requirement." },
  { label: "Define", copy: "Translate needs into scope, workflows and integrations." },
  { label: "Design", copy: "Shape the experience and system architecture." },
  { label: "Build", copy: "Develop with structured progress reviews." },
  { label: "Launch", copy: "Review, test and release." },
  { label: "Evolve", copy: "Support, monitor and improve over time." },
];

export default function HowWeWorkPreview() {
  return (
    <section
      id="how-we-work"
      data-testid="how-we-work-section"
      className="relative scroll-mt-20 overflow-hidden bg-[#070604] py-20 sm:py-24"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[35vh] w-[60vw] -translate-x-1/2 rounded-full bg-[#B8962E]/[0.07] blur-[130px]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag index="09">How We Work</SectionTag>
        </Reveal>
        <h2
          data-testid="how-we-work-headline"
          className="mt-6 max-w-2xl font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
        >
          <MaskedLines delay={0.1}>
            A structured path
            <br />
            from requirement
            <br />
            <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
              to delivery.
            </span>
          </MaskedLines>
        </h2>

        {/* connected execution timeline */}
        <div data-testid="how-we-work-steps" className="relative mt-16">
          <motion.span
            className="absolute left-0 right-0 top-[3px] hidden h-px origin-left bg-gradient-to-r from-[#E7CE8B] via-[#D4AF37]/50 to-white/10 lg:block"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.2 }}
            aria-hidden="true"
          />
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-6 lg:gap-7">
            {STEPS.map((s, i) => {
              const last = i === STEPS.length - 1;
              return (
                <Reveal key={s.label} delay={0.12 + i * 0.14}>
                  <div
                    data-testid={`how-we-work-step-${i + 1}`}
                    className="group relative border-l border-[#D4AF37]/25 pl-6 lg:border-l-0 lg:pl-0"
                  >
                    <span
                      className={`absolute -left-[4px] top-0 h-[7px] w-[7px] rotate-45 lg:hidden ${last ? "bg-[#E7CE8B]" : "border border-[#D4AF37]/60 bg-[#070604]"}`}
                      aria-hidden="true"
                    />
                    <span className="mb-6 hidden lg:block" aria-hidden="true">
                      <motion.span
                        className={`block h-[7px] w-[7px] rotate-45 ${last ? "bg-[#E7CE8B]" : "border border-[#D4AF37]/60 bg-[#070604] transition-colors duration-500 group-hover:bg-[#D4AF37]"}`}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.5, ease: EASE, delay: 0.5 + i * 0.22 }}
                      />
                    </span>
                    <span className="font-serif text-3xl font-normal leading-none tracking-tight text-white/[0.07] transition-colors duration-700 group-hover:text-[#D4AF37]/20" aria-hidden="true">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p
                      className={`mt-4 font-serif text-lg font-normal uppercase tracking-tight transition-colors duration-500 ${
                        last ? "text-[#E7CE8B]" : "text-slate-100 group-hover:text-white"
                      }`}
                    >
                      {s.label}
                    </p>
                    <p className="mt-3 text-[13px] font-normal leading-relaxed text-stone-400">
                      {s.copy}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.3}>
          <ArrowLink to="/how-it-works" testid="how-we-work-link">
            See How It Works
          </ArrowLink>
        </Reveal>
      </div>
    </section>
  );
}
